import React, { useMemo, useState } from "react";
import {
    Box,
    Collapse,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

const MONTHS = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
];

function LeftPlaysPanel({ startYear = 2025, onSelectMonth }) {

    const years = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const arr = [];
        for (let y = Math.max(startYear, 1); y <= currentYear; y++) arr.push(y);
        return arr;
    }, [startYear]);

    const [openYears, setOpenYears] = useState(() => {
        const currentYear = new Date().getFullYear();
        return { [currentYear]: true };
    });

    const [selected, setSelected] = useState(() => {
        const d = new Date();
        return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });

    const toggleYear = (year) => {
        setOpenYears((prev) => ({ ...prev, [year]: !prev[year] }));
        setSelected((prev) => ({ ...prev, year }));
    };

    const pickMonth = (year, month) => {
        setSelected({ year, month });
        onSelectMonth?.({ year, month });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid rgba(15, 34, 77, 0.10)",
                background: "linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%)",
                boxShadow: "0 12px 28px rgba(15, 34, 77, 0.08)",
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid rgba(15, 34, 77, 0.08)",
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 900,
                        color: "#10285f",
                        letterSpacing: 0.4,
                    }}
                >
                    Filtros por fecha
                </Typography>

                <Typography
                    variant="caption"
                    sx={{
                        color: "rgba(15, 34, 77, 0.6)",
                        fontWeight: 600,
                    }}
                >
                    Selecciona un año y mes
                </Typography>
            </Box>

            {/* LIST */}
            <Box
                sx={{
                    maxHeight: "65vh",
                    overflow: "auto",
                    py: 0.5,
                }}
            >
                <List dense disablePadding>

                    {years.map((year) => {
                        const isOpen = !!openYears[year];
                        const isYearSelected = selected.year === year;

                        return (
                            <Box key={year}>

                                {/* YEAR */}
                                <ListItemButton
                                    onClick={() => toggleYear(year)}
                                    selected={isYearSelected}
                                    sx={{
                                        px: 2,
                                        py: 0.8,
                                        transition: "0.2s ease",

                                        "&.Mui-selected": {
                                            background: "rgba(214, 170, 58, 0.18)",
                                            borderLeft: "3px solid #d6aa3a",
                                        },

                                        "&:hover": {
                                            background: "rgba(16, 40, 95, 0.06)",
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <DateRangeOutlinedIcon
                                            sx={{
                                                fontSize: 18,
                                                color: isYearSelected ? "#d6aa3a" : "#10285f",
                                            }}
                                        />
                                    </ListItemIcon>

                                    <ListItemText
                                        primary={year}
                                        primaryTypographyProps={{
                                            fontWeight: 800,
                                            fontSize: "0.9rem",
                                            color: "#10285f",
                                        }}
                                    />

                                    {isOpen ? (
                                        <ExpandLess sx={{ color: "#10285f" }} />
                                    ) : (
                                        <ExpandMore sx={{ color: "#10285f" }} />
                                    )}
                                </ListItemButton>

                                {/* MONTHS */}
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <List dense disablePadding sx={{ pl: 1 }}>

                                        {MONTHS.map((m) => {
                                            const isMonthSelected =
                                                selected.year === year && selected.month === m.value;

                                            return (
                                                <ListItemButton
                                                    key={`${year}-${m.value}`}
                                                    onClick={() => pickMonth(year, m.value)}
                                                    selected={isMonthSelected}
                                                    sx={{
                                                        mx: 1,
                                                        my: 0.2,
                                                        py: 0.6,
                                                        borderRadius: 2,
                                                        transition: "0.2s ease",

                                                        "&.Mui-selected": {
                                                            background: "linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)",
                                                            color: "#fff",
                                                            boxShadow: "0 4px 12px rgba(16,40,95,0.25)",
                                                        },

                                                        "&:hover": {
                                                            background: "rgba(214, 170, 58, 0.12)",
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                        <CalendarMonthOutlinedIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: isMonthSelected ? "#fff" : "#10285f",
                                                            }}
                                                        />
                                                    </ListItemIcon>

                                                    <ListItemText
                                                        primary={m.label}
                                                        primaryTypographyProps={{
                                                            fontSize: "0.8rem",
                                                            fontWeight: isMonthSelected ? 800 : 600,
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Collapse>

                            </Box>
                        );
                    })}

                </List>
            </Box>
        </Paper>
    );
}

export default LeftPlaysPanel;